import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { getAppProps } from '@pages/_app';
import { buildDataUrl, buildFilterQuery } from '@helper/api';
import BackToTop from '@components/molecules/back-to-top/backToTop';
import ShapedStage from '@components/organisms/shaped-stage/shapedStage';
import Stakeholder from '@components/organisms/stakeholder/stakeholder';
import TemplateDefault from '../../templates/default/default';

const CurrentInfo = dynamic(() => import('@components/molecules/current-info/currentInfo'), {
  loading: () => null
});

const ExhibitionTabTeaser = dynamic(
  () => import('@components/organisms/exhibition-tab-teaser/exhibitionTabTeaser'),
  { loading: () => null }
);

const TargetGroups = dynamic(() => import('@components/organisms/target-groups/targetGroups'), {
  loading: () => null
});

const TeaserContainerShaped = dynamic(
  () => import('@components/organisms/teaser-container-shaped/teaserContainerShaped'),
  { loading: () => null }
);

const HeroTeaser = dynamic(() => import('@components/organisms/hero-teaser/heroTeaser'), {
  loading: () => null
});

const NewsletterTeaser = dynamic(
  () => import('@components/organisms/newsletter-teaser/newsletterTeaser'),
  { loading: () => null }
);

const EventTeaserList = dynamic(
  () => import('@components/organisms/event-teaser-list/eventTeaserList'),
  { loading: () => null }
);

const anchorNavEntries = [];

const addAnchorNavEntry = ({ data, currentInfo }) => {
  const entry = {};
  switch (data.typeHandle) {
    case 'currentInfo':
      entry.title = currentInfo?.data?.[0]?.headline || '';
      entry.id = data.id;
      break;
    case 'exhibitionTeasers':
      entry.title = data.teasers[0]?.tabTitle;
      entry.id = data.id;
      break;
    case 'targetGroups':
      entry.title = data.headline;
      entry.id = data.id;
      break;
    case 'teaserContainer':
      entry.title = data.teaserContainerHeadline;
      entry.id = data.id;
      break;
    case 'eventTeaser':
      entry.title = data.eventTeaserHeadline;
      entry.id = data.id;
      break;
    default:
      return;
  }
  const entryExists = anchorNavEntries.some(e => e.id === entry.id);
  if (!entryExists) {
    anchorNavEntries.push(entry);
  }
};

const sectionRenderer = ({ data, loginMask, events, currentInfo }) => {
  switch (data.typeHandle) {
    case 'currentInfo':
      if (!currentInfo && !currentInfo.data[0]) return null;
      return <CurrentInfo {...currentInfo?.data?.[0]} data={data} />;
    case 'exhibitionTeasers':
      return <ExhibitionTabTeaser data={data} loginMask={loginMask} />;
    case 'targetGroups':
      return <TargetGroups data={data} />;
    case 'teaserContainer':
      return <TeaserContainerShaped data={data} />;
    case 'featuredContent':
      return <HeroTeaser data={data} />;
    case 'subscribeToNewsletter':
      return <NewsletterTeaser data={data} />;
    case 'eventTeaser':
      return <EventTeaserList data={data} events={events} />;
    default:
      return null;
  }
};

const HomeTime = ({ data = {}, page = 1 }) => {
  const currentPage = parseInt(page, 10);
  const stageType = data?.stageType || 'default';
  const hasCurtains = !!data?.hasCurtains;
  const showAnchorLinks = data?.graphql?.page?.stage?.[0]?.showAnchorLinks;
  const homepageModules = data?.graphql?.page?.homepageModules || [];
  const stageData = data?.graphql?.page?.stage?.[0];

  // RENDER ANCHOR NAVIGATION
  if (showAnchorLinks && homepageModules.length > 0) {
    homepageModules.forEach(item =>
      addAnchorNavEntry({ data: item, currentInfo: data.currentInfoModule })
    );
  }

  return (
    <TemplateDefault
      noLogo={currentPage === 1 && stageType === 'default'}
      invertedHeader={currentPage === 1 && stageType === 'campaign'}
      hasFilter
      hasCurtain={currentPage === 1 && hasCurtains}
    >
      {stageData && <ShapedStage anchorNav={anchorNavEntries} data={stageData} />}
      {homepageModules.length > 0 ? (
        homepageModules.map(item =>
          sectionRenderer({
            data: item,
            loginMask: data.loginMask,
            events: data.events?.events || [],
            currentInfo: data.currentInfoModule
          })
        )
      ) : (
        <p>No content available</p>
      )}
      {data?.stakeholders?.logos?.length > 0 && <Stakeholder {...data.stakeholders} />}
      <BackToTop />
    </TemplateDefault>
  );
};

HomeTime.propTypes = {
  page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape({
    stakeholders: PropTypes.shape(),
    loginMask: PropTypes.shape(),
    time: PropTypes.string.isRequired,
    hasCurtains: PropTypes.bool,
    stageType: PropTypes.string,
    currentInfoModule: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape())
    }),
    events: PropTypes.shape({
      events: PropTypes.arrayOf(PropTypes.shape())
    }),
    pageModules: PropTypes.arrayOf(PropTypes.shape()),
    graphql: PropTypes.shape({
      page: PropTypes.shape({
        stage: PropTypes.arrayOf(PropTypes.shape()),
        homepageModules: PropTypes.arrayOf(PropTypes.shape())
      })
    })
  })
};

const getHomeTimeProps = async ({ query, locale, uri, NEXT_PUBLIC_JSON_API_PATH }) => {
  const queryString = buildFilterQuery(query['filters[]'], query.page);
  console.log(uri);
  const getPageProps = fetch(buildDataUrl(`${locale || 'de'}/${NEXT_PUBLIC_JSON_API_PATH}/${uri}`, queryString))
    .then(res => res.json())
    .catch(err => console.error(err));

  const [appProps, data] = await Promise.allSettled([getAppProps({ locale }), getPageProps]).then(
    results => results.map(result => result.value)
  );

  return {
    PAGE_TYPE: 'TIME',
    ...appProps,
    ...data,
    page: query.page || '1'
  };
};

export default HomeTime;
export { getHomeTimeProps };
